import {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Redirect,
  Route,
  Switch,
  matchPath,
  useHistory,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { Box, CircularProgress } from "@mui/material";
import { EMPTY_CONTEXT, ENGLISH_ABBREV } from "@bto/shared";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { UpfrontPaymentMultiple } from "../components/UpfrontPayment/UpfrontPaymentMultiple";
import {
  HELP_PAGE_URL,
  EMAIL_REPORT_URL_PREFIX,
  LANDING_PAGE_URL,
  LOGIN_PAGE_URL,
  PROFILE_URL,
  REGISTER_URL,
  VERIFY_EMAIL_URL,
  VERIFY_EMAIL_SEND_AGAIN_URL,
  VERIFY_EMAIL_CONFIRMED_URL,
  RESET_PASSWORD_URL,
  RESET_PASSWORD_SENT_URL,
  ANEUPLOIDY_PAGE_URL,
  CARRIER_PAGE_URL,
  CFC_PAGE_URL,
  CONTACT_US_URL,
  SCHEDULING_URL,
  GC_LEARN_MORE_URL,
  EXTERNAL_FORWARD_FETAL_SEX,
  PRETEST_REGISTER_URL,
  BILLING_URL,
  BABYPEEK_URL,
  LEGACY_FREE_TRAIT_BABYPEEK_URL,
  LEGACY_PUBLIC_BABYPEEK_URL,
  TEST_INFO_URL,
  BABYPEEK_URL_FAQ,
  TERMS_OF_USE_URL,
  BABYPEEK_URL_FEEDBACK,
  BILLING_MULTIPLE_URL,
  VIDEO_PAGE_URL,
  PHLEBOTOMY_INTAKE_URL,
  PHLEBOTOMY_LOCATION_FINDER_URL,
  PHLEBOTOMY_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_MOBILE_REQUEST_URL,
  PHLEBOTOMY_MOBILE_SCHEDULE_URL,
  PHLEBOTOMY_SEARCH_URL,
  PHLEBOTOMY_PHONE_OPTIONS_URL,
  PHLEBOTOMY_V1_INTAKE_URL,
  PHLEBOTOMY_V1_LOCATION_FINDER_URL,
  PHLEBOTOMY_V1_SEARCH_URL,
  PHLEBOTOMY_V1_PHONE_OPTIONS_URL,
  PHLEBOTOMY_V1_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_V1_MOBILE_REQUEST_URL,
  PHLEBOTOMY_LEGACY_HUB_URL,
  PHLEBOTOMY_LEGACY_SEARCH_URL,
  PHLEBOTOMY_LEGACY_REMINDER_URL,
  PHLEBOTOMY_LEGACY_LOCATION_FINDER_URL,
  PHLEBOTOMY_LEGACY_PHONE_OPTIONS_URL,
  PHLEBOTOMY_LEGACY_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_LEGACY_MOBILE_REQUEST_URL,
  PORTAL_ACCOUNT_PUBLIC_ID_ROUTE_PATTERN,
  NIPT_CONSENT,
} from "../utils/urlConstants";
import Help from "../pages/Help";
import Landing from "../pages/Landing";
import CreateAccountPage from "../pages/CreateAccount";
import VerifyEmailPage from "../pages/VerifyEmail";
import VerifyEmailSendAgain from "../pages/VerifyEmailSendAgain";
import VerifyEmailConfirmedPage from "../pages/VerifyEmailConfirmed";
import LoginPage from "../pages/Login";
import TermsOfServicePage from "../pages/TermsOfService";
import ResetPasswordPage from "../pages/ResetPassword";
import ResetPasswordSentPage from "../pages/ResetPasswordSent";
import AneuploidyPage from "../pages/Aneuploidy";
import CarrierPage from "../pages/Carrier";
import CfcPage from "../pages/Cfc/Cfc";
import ProfilePage from "../pages/Profile";
import ContactUsPage from "../pages/ContactUs";
import SchedulingPage from "../pages/Scheduling";
import GCLearnMore from "../pages/GCLearnMore/GCLearnMore";
import ExternalForwardFetalSex from "../components/ExternalForwardFetalSex/ExternalForwardFetalSex";
import { Header } from "../components/Header/Header";
import { Footer } from "../components/Footer/Footer";
import { ChatbotWidget } from "../components/ChatBot/ChatBotWidget";
import { FailedPaymentChecker } from "../components/FailedPaymentChecker";
import { patientState } from "../recoil/atoms";
import CreateAccountPagePreTest from "../pages/CreateAccount/CreateAccountPreTest";
import { UpfrontPayment } from "../components/UpfrontPayment/Index";
import { BabyPeek } from "../components/BabyPeek/BabyPeek";
import { ValidationTesting } from "../components/ValidationTesting/ValidationTesting";
import LegacyPhlebotomyRedirectPage from "../pages/Phlebotomy/LegacyPhlebotomyRedirectPage";
import LegacyPhlebotomyReminderRedirectPage from "../pages/Phlebotomy/LegacyPhlebotomyReminderRedirectPage";
import PhlebotomySearchPage from "../pages/Phlebotomy/Search";
import MobilePhlebotomyOptionsPage from "../pages/Phlebotomy/Search/MobilePhlebotomyOptionsPage";
import MobilePhlebotomyGetLabsSchedulePage from "../pages/Phlebotomy/Search/MobilePhlebotomyGetLabsSchedulePage";
import PhlebotomyUnavailablePage from "../pages/Phlebotomy/PhlebotomyUnavailablePage";
import PublicConsent from "../pages/PublicConsent";
import { BabyPeekFAQ } from "../components/BabyPeek/BabyPeekFAQ";
import { BabyPeekSingleFeedback } from "../components/BabyPeek/BabyPeekSingleFeedback";
import VideoPage from "../pages/Video/VideoPage";
import { ProtectedRoute } from "./ProtectedRoute";
import {
  useVariantFeatureFlag,
  useBooleanFeatureFlag,
} from "../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../utils/launchDarklyUtils";
import {
  normalizeLanguage,
  persistLanguagePreference,
} from "../i18n/languagePreference";

const PUBLIC_LANGUAGE_ROUTES = [
  LOGIN_PAGE_URL,
  RESET_PASSWORD_URL,
  RESET_PASSWORD_SENT_URL,
  TERMS_OF_USE_URL,
  EXTERNAL_FORWARD_FETAL_SEX,
  NIPT_CONSENT,
];

const STANDALONE_PHLEBOTOMY_PATH_PREFIXES = [
  PHLEBOTOMY_INTAKE_URL,
  PHLEBOTOMY_SEARCH_URL,
  PHLEBOTOMY_LOCATION_FINDER_URL,
  PHLEBOTOMY_PHONE_OPTIONS_URL,
  PHLEBOTOMY_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_MOBILE_REQUEST_URL,
  PHLEBOTOMY_MOBILE_SCHEDULE_URL,
  PHLEBOTOMY_V1_INTAKE_URL,
  PHLEBOTOMY_V1_SEARCH_URL,
  PHLEBOTOMY_V1_LOCATION_FINDER_URL,
  PHLEBOTOMY_V1_PHONE_OPTIONS_URL,
  PHLEBOTOMY_V1_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_V1_MOBILE_REQUEST_URL,
  PHLEBOTOMY_LEGACY_HUB_URL,
  PHLEBOTOMY_LEGACY_SEARCH_URL,
  PHLEBOTOMY_LEGACY_REMINDER_URL,
  PHLEBOTOMY_LEGACY_LOCATION_FINDER_URL,
  PHLEBOTOMY_LEGACY_PHONE_OPTIONS_URL,
  PHLEBOTOMY_LEGACY_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_LEGACY_MOBILE_REQUEST_URL,
] as const;

/** Match exact blood-draw paths and their subpaths without false-matching siblings
 *  (e.g. `/phlebotomy` must not match `/phlebotomy-search`). Nested `/blooddraw/*`
 *  paths are safe under the `/blooddraw` prefix. */
const isStandalonePhlebotomyPath = (pathname: string): boolean =>
  STANDALONE_PHLEBOTOMY_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const CareTeamMobilePhlebotomySchedulePage = (): ReactElement => (
  <MobilePhlebotomyGetLabsSchedulePage forceCareTeam />
);

const GetLabsScheduleFlagLoading = (): ReactElement => (
  <Box display="grid" minHeight="50vh" sx={{ placeItems: "center" }}>
    <CircularProgress aria-label="Loading schedule" />
  </Box>
);

const AppContainer = (): ReactElement => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const [pathname, setPathname] = useState(history.location.pathname);
  const [patientInfo] = useRecoilState(patientState);
  const emailPrefix = pathname.startsWith(EMAIL_REPORT_URL_PREFIX);
  const { variant: chatbotVariant } = useVariantFeatureFlag(
    FeatureFlags.PatientPortalChatbot,
  );
  const { enabled: isAggressiveChatbot } = useBooleanFeatureFlag(
    FeatureFlags.IsAggressiveChatbot,
  );
  const { enabled: isCfcEnabled } = useBooleanFeatureFlag(FeatureFlags.Cfc);
  const {
    enabled: showLanguageSwitcher,
    loading: showLanguageSwitcherLoading,
  } = useBooleanFeatureFlag(
    FeatureFlags.PatientPortalSpanishLanguageToggle,
    EMPTY_CONTEXT,
    // Pre-auth (signup / verify) there's no patient, so fall back to the
    // LDProvider's anonymous key. Without this the flag resolves false on those
    // public routes and shouldForceEnglish wrongly flips a ?lang=es session to
    // English. Authenticated requests still evaluate against the patient's id.
    patientInfo?.auth0?.user_id ?? "anonymous",
  );
  const flags = useFlags();
  const ldClient = useLDClient();
  // Read the flag value straight from useFlags (not the hook's `enabled`, which
  // lags a commit behind in a passive effect) so "resolved" and "on" come from
  // the same render tick.
  const isLanguageSwitcherFlagResolved = Object.prototype.hasOwnProperty.call(
    flags,
    FeatureFlags.PatientPortalSpanishLanguageToggle,
  );
  const isLanguageSwitcherFlagOn =
    flags[FeatureFlags.PatientPortalSpanishLanguageToggle] === true;
  // Kill switch, not a launch gate: the finder is on unless LaunchDarkly says
  // otherwise, so a missing or not-yet-resolved flag still serves the page.
  // `=== true` would drop cold deep links, because the first paint has no flags
  // yet and the catch-all would redirect to login before LD resolves.
  const isPhlebotomyFinderEnabled =
    flags[FeatureFlags.PatientPhlebotomySearch] !== false;
  const isGetLabsSandboxUiEnabled =
    (import.meta.env.VITE_ENV === "staging" ||
      import.meta.env.VITE_ENV === "local") &&
    flags[FeatureFlags.PatientPhlebotomyGetLabsSandbox] === true;
  const isGetLabsSandboxFlagRelevant =
    import.meta.env.VITE_ENV === "staging" ||
    import.meta.env.VITE_ENV === "local";
  const isGetLabsSandboxFlagResolved =
    Object.prototype.hasOwnProperty.call(
      flags,
      FeatureFlags.PatientPhlebotomyGetLabsSandbox,
    ) ||
    // LDProvider exposes the client after initialization or timeout, even if
    // the evaluated flag key is absent.
    Boolean(ldClient);
  const mobileScheduleRoute = (() => {
    if (!isPhlebotomyFinderEnabled) {
      return PhlebotomyUnavailablePage;
    }
    if (isGetLabsSandboxFlagRelevant && !isGetLabsSandboxFlagResolved) {
      return GetLabsScheduleFlagLoading;
    }
    if (isGetLabsSandboxUiEnabled) {
      return MobilePhlebotomyGetLabsSchedulePage;
    }
    return CareTeamMobilePhlebotomySchedulePage;
  })();
  // The signup (/c/s) and verify-email route family are anonymous / early-auth:
  // the client can't evaluate the lims-targeted flag (no lims_id pre-auth, and
  // the LD client hasn't re-identified yet right after signup). The verify-email
  // prefix intentionally includes /client/verifyEmailConfirm and
  // /client/verifyEmailSendAgain, since those pages still belong to the same
  // signup/verification journey. Signup resolves the flag server-side and carries
  // the chosen language through verification, so AppContainer does not force
  // English across that whole journey — it would otherwise revert a Spanish
  // session on the post-signup verify-email step.
  const onSignupOrVerifyEmailRoute =
    pathname.startsWith(REGISTER_URL) || pathname.startsWith(VERIFY_EMAIL_URL);
  const isStandalonePhlebotomyRoute = isStandalonePhlebotomyPath(pathname);
  const isPublicBillingRoute = pathname.startsWith(`${BILLING_URL}/`);
  const isPublicLanguageRoute =
    PUBLIC_LANGUAGE_ROUTES.some((route) => pathname.startsWith(route)) ||
    isPublicBillingRoute ||
    isStandalonePhlebotomyRoute ||
    onSignupOrVerifyEmailRoute;
  const shouldPreserveSessionLanguage =
    isPublicLanguageRoute &&
    !(isPublicBillingRoute && Boolean(patientInfo?.auth0?.user_id));
  const canEvaluateLanguagePreference =
    isPublicLanguageRoute || Boolean(patientInfo?.auth0?.user_id);
  const selectedLanguage = i18n.language.split("-")[0];
  // Portal-wide: non-English only runs while the Spanish toggle flag is on. A
  // stored preference (switcher or ?lang= param) survives in localStorage so
  // it takes effect as soon as the flag is enabled.
  // Public phlebotomy is exempt (`!isStandalonePhlebotomyRoute`): those pages
  // and their footers (`requireLanguageFeatureFlag={false}`) keep Spanish and
  // the switcher available even when the flag is off, matching SMS/QR traffic
  // that already offers Spanish outside the portal-wide launch gate.
  const shouldForceEnglish =
    canEvaluateLanguagePreference &&
    isLanguageSwitcherFlagResolved &&
    !isLanguageSwitcherFlagOn &&
    !isStandalonePhlebotomyRoute &&
    !onSignupOrVerifyEmailRoute &&
    selectedLanguage !== ENGLISH_ABBREV;

  const setPathnameCheck = () => {
    setPathname(history.location.pathname);
  };

  useLayoutEffect(() => {
    if (shouldForceEnglish) {
      i18n.changeLanguage(ENGLISH_ABBREV);
    }
  }, [i18n, shouldForceEnglish]);

  // The patient's saved profile preference sets the portal UI language for the
  // authenticated app (saved profile preference determines portal language),
  // gated on the toggle flag. It is intentionally skipped on anonymous public /
  // signup / verification routes, where the session language (the `?lang=` param
  // carried through signup) must win — otherwise the default "en" profile would
  // override a Spanish signup flow. Authenticated billing visitors still receive
  // their saved preference. Applied once per distinct saved value via a ref guard,
  // so an in-session switcher change is never overridden.
  const savedLanguagePreference = patientInfo?.user?.language;
  const appliedSavedLanguageRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !showLanguageSwitcher ||
      showLanguageSwitcherLoading ||
      shouldPreserveSessionLanguage ||
      !savedLanguagePreference
    ) {
      return;
    }
    const normalized = normalizeLanguage(savedLanguagePreference);
    // Only a non-default (Spanish) saved preference is actively applied. English
    // is the default display, so applying a saved "en" would wrongly override a
    // still-in-session Spanish choice — e.g. right after a Spanish signup, which
    // by design does not persist the language until the patient opts in. English-
    // preferring patients still see English because it's the default.
    if (normalized === ENGLISH_ABBREV) {
      return;
    }
    if (appliedSavedLanguageRef.current === normalized) {
      return;
    }
    appliedSavedLanguageRef.current = normalized;
    // Reached on login when the saved Spanish preference doesn't yet match the
    // displayed language — e.g. a returning patient on a fresh device (empty
    // localStorage defaults to English), or the first load after the flag enables.
    if (normalized !== i18n.language.split("-")[0]) {
      i18n.changeLanguage(normalized);
      persistLanguagePreference(normalized);
    }
  }, [
    savedLanguagePreference,
    showLanguageSwitcher,
    showLanguageSwitcherLoading,
    shouldPreserveSessionLanguage,
    i18n,
  ]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      window.scrollTo(0, 0);
      setPathnameCheck();
    });
    return () => {
      unlisten();
    };
  }, []);

  const showAuthenticatedChrome = Boolean(
    pathname !== LOGIN_PAGE_URL &&
    pathname !== EXTERNAL_FORWARD_FETAL_SEX &&
    !pathname.startsWith(REGISTER_URL) &&
    !pathname.startsWith(VERIFY_EMAIL_URL) &&
    !emailPrefix &&
    !isStandalonePhlebotomyRoute &&
    patientInfo?.auth0,
  );
  const showFooter = showAuthenticatedChrome || isPublicBillingRoute;

  return (
    <>
      {(showAuthenticatedChrome ||
        matchPath(pathname, { path: HELP_PAGE_URL, exact: true })) && (
        <Header />
      )}
      <FailedPaymentChecker />
      <Switch>
        <Route
          exact
          path={LOGIN_PAGE_URL}
          render={() => <LoginPage setPathname={setPathnameCheck} />}
        />
        <Route
          exact
          path={TERMS_OF_USE_URL}
          render={() => <TermsOfServicePage />}
        />
        <Route exact path={VERIFY_EMAIL_URL} component={VerifyEmailPage} />
        <Route
          exact
          path={`${REGISTER_URL}`}
          render={() => <CreateAccountPage setPathname={setPathnameCheck} />}
        />
        <Route
          exact
          path={`${PRETEST_REGISTER_URL}/:portalAccountPublicId?`}
          render={() => (
            <CreateAccountPagePreTest setPathname={setPathnameCheck} />
          )}
        />
        <Route
          exact
          path={`${REGISTER_URL}/:smsCode`}
          render={() => <CreateAccountPage setPathname={setPathnameCheck} />}
        />
        <Route exact path={VERIFY_EMAIL_URL} component={VerifyEmailPage} />
        <Route
          exact
          path={`${VERIFY_EMAIL_CONFIRMED_URL}/:verifyJwt`}
          component={VerifyEmailConfirmedPage}
        />
        <Route
          exact
          path={VERIFY_EMAIL_SEND_AGAIN_URL}
          component={VerifyEmailSendAgain}
        />
        <Route exact path={RESET_PASSWORD_URL} component={ResetPasswordPage} />
        <Route
          exact
          path={RESET_PASSWORD_SENT_URL}
          component={ResetPasswordSentPage}
        />
        <Route
          exact
          path={`${EXTERNAL_FORWARD_FETAL_SEX}/:base64PatientName/:base64FetalSex`}
          component={ExternalForwardFetalSex}
        />
        <Route exact path={HELP_PAGE_URL} component={Help} />
        <ProtectedRoute exact path={LANDING_PAGE_URL} component={Landing} />
        <ProtectedRoute exact path={PROFILE_URL} component={ProfilePage} />
        <ProtectedRoute exact path={CONTACT_US_URL} component={ContactUsPage} />
        <Route exact path={SCHEDULING_URL} component={SchedulingPage} />
        <ProtectedRoute
          exact
          path={GC_LEARN_MORE_URL}
          component={GCLearnMore}
        />
        <ProtectedRoute exact path={VIDEO_PAGE_URL} component={VideoPage} />
        <ProtectedRoute
          exact
          path={`${ANEUPLOIDY_PAGE_URL}/:requisitionID`}
          component={AneuploidyPage}
        />
        <ProtectedRoute
          exact
          path={`${CARRIER_PAGE_URL}/:requisitionID`}
          component={CarrierPage}
        />
        {isCfcEnabled && (
          <ProtectedRoute
            exact
            path={`${CFC_PAGE_URL}/:requisitionID`}
            component={CfcPage}
          />
        )}
        <Route
          exact
          path={`${BILLING_URL}/:billingPK/:adminID?`}
          render={({ match }) => <UpfrontPayment key={match.url} />}
        />
        <ProtectedRoute
          exact
          path={BILLING_MULTIPLE_URL}
          component={UpfrontPaymentMultiple}
        />
        <Route
          exact
          path={`${LEGACY_PUBLIC_BABYPEEK_URL}/:babypeekPK/:pageIndex?`}
          render={({ location, match }) => (
            <Redirect
              to={`${BABYPEEK_URL}/${match.params.babypeekPK}${
                match.params.pageIndex ? `/${match.params.pageIndex}` : ""
              }${location.search}`}
            />
          )}
        />
        <Route
          exact
          path={`${LEGACY_FREE_TRAIT_BABYPEEK_URL}/:babypeekPK`}
          render={({ location, match }) => (
            <Redirect
              to={`${BABYPEEK_URL}/${match.params.babypeekPK}${location.search}`}
            />
          )}
        />
        <ProtectedRoute
          exact
          path={`${BABYPEEK_URL}/:babypeekPK?/:pageIndex?`}
          component={BabyPeek}
        />
        <Route exact path={`${BABYPEEK_URL_FAQ}`} component={BabyPeekFAQ} />
        <Route
          exact
          path={`${BABYPEEK_URL_FEEDBACK}/:babypeekPK?`}
          component={BabyPeekSingleFeedback}
        />
        <Route
          exact
          path={`${TEST_INFO_URL}/:reqPK?`}
          component={ValidationTesting}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_LEGACY_HUB_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_PHONE_OPTIONS_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_LOCATION_FINDER_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        {/* These stay registered whatever the kill switch says. Reminder intake,
            browser reloads, and previously delivered links all land here, and a
            missing route would send them to login instead of an explanation. */}
        <Route
          exact
          path={PHLEBOTOMY_SEARCH_URL}
          component={
            isPhlebotomyFinderEnabled
              ? PhlebotomySearchPage
              : PhlebotomyUnavailablePage
          }
        />
        <Route
          exact
          path={PHLEBOTOMY_MOBILE_OPTIONS_URL}
          component={
            isPhlebotomyFinderEnabled
              ? MobilePhlebotomyOptionsPage
              : PhlebotomyUnavailablePage
          }
        />
        <Route
          exact
          path={PHLEBOTOMY_MOBILE_REQUEST_URL}
          component={
            isPhlebotomyFinderEnabled
              ? CareTeamMobilePhlebotomySchedulePage
              : PhlebotomyUnavailablePage
          }
        />
        <Route
          exact
          path={PHLEBOTOMY_MOBILE_SCHEDULE_URL}
          component={mobileScheduleRoute}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_INTAKE_URL}/provider/:portalAccountPublicId?`}
          component={
            isPhlebotomyFinderEnabled
              ? PhlebotomySearchPage
              : PhlebotomyUnavailablePage
          }
        />
        <Route
          exact
          path={PHLEBOTOMY_INTAKE_URL}
          component={
            isPhlebotomyFinderEnabled
              ? PhlebotomySearchPage
              : PhlebotomyUnavailablePage
          }
        />
        <Route
          exact
          path={`${PHLEBOTOMY_INTAKE_URL}/:portalAccountPublicId${PORTAL_ACCOUNT_PUBLIC_ID_ROUTE_PATTERN}`}
          render={({ match, location }) => {
            const query = new URLSearchParams(location.search);
            query.set("clinic", match.params.portalAccountPublicId);
            return (
              <Redirect
                to={{
                  pathname: PHLEBOTOMY_INTAKE_URL,
                  search: `?${query.toString()}`,
                }}
              />
            );
          }}
        />
        {/* Anything else directly under /blooddraw is a retired path segment, a
            truncated link, or a typo. Send it to the universal finder rather
            than the landing page, and never read it as a clinic scope. */}
        <Route
          exact
          path={`${PHLEBOTOMY_INTAKE_URL}/:unmatched`}
          render={({ location }) => (
            <Redirect
              to={{ pathname: PHLEBOTOMY_SEARCH_URL, search: location.search }}
            />
          )}
        />
        <Route
          exact
          path={PHLEBOTOMY_V1_SEARCH_URL}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: PHLEBOTOMY_SEARCH_URL,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_V1_INTAKE_URL}/provider/:portalAccountPublicId?`}
          render={({ match, location }) => (
            <Redirect
              to={{
                pathname: match.params.portalAccountPublicId
                  ? `${PHLEBOTOMY_INTAKE_URL}/provider/${match.params.portalAccountPublicId}`
                  : `${PHLEBOTOMY_INTAKE_URL}/provider`,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_V1_INTAKE_URL}/:portalAccountPublicId${PORTAL_ACCOUNT_PUBLIC_ID_ROUTE_PATTERN}?`}
          render={({ match, location }) => {
            const query = new URLSearchParams(location.search);
            if (match.params.portalAccountPublicId) {
              query.set("clinic", match.params.portalAccountPublicId);
            }
            const search = query.toString();
            return (
              <Redirect
                to={{
                  pathname: PHLEBOTOMY_INTAKE_URL,
                  search: search ? `?${search}` : "",
                }}
              />
            );
          }}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_V1_INTAKE_URL}/:unmatched`}
          render={({ location }) => (
            <Redirect
              to={{ pathname: PHLEBOTOMY_SEARCH_URL, search: location.search }}
            />
          )}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_V1_LOCATION_FINDER_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_V1_PHONE_OPTIONS_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        <Route
          exact
          path={PHLEBOTOMY_V1_MOBILE_OPTIONS_URL}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: PHLEBOTOMY_MOBILE_OPTIONS_URL,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={PHLEBOTOMY_V1_MOBILE_REQUEST_URL}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: PHLEBOTOMY_MOBILE_REQUEST_URL,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={PHLEBOTOMY_LEGACY_SEARCH_URL}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: PHLEBOTOMY_SEARCH_URL,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={PHLEBOTOMY_LEGACY_MOBILE_OPTIONS_URL}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: PHLEBOTOMY_MOBILE_OPTIONS_URL,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={PHLEBOTOMY_LEGACY_MOBILE_REQUEST_URL}
          render={({ location }) => (
            <Redirect
              to={{
                pathname: PHLEBOTOMY_MOBILE_REQUEST_URL,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_LEGACY_REMINDER_URL}/provider/:portalAccountPublicId?`}
          render={({ match, location }) => (
            <Redirect
              to={{
                pathname: match.params.portalAccountPublicId
                  ? `${PHLEBOTOMY_INTAKE_URL}/provider/${match.params.portalAccountPublicId}`
                  : `${PHLEBOTOMY_INTAKE_URL}/provider`,
                search: location.search,
              }}
            />
          )}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_LEGACY_REMINDER_URL}/patient/:legacyAccountId?`}
          component={LegacyPhlebotomyReminderRedirectPage}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_LEGACY_LOCATION_FINDER_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        <Route
          exact
          path={`${PHLEBOTOMY_LEGACY_PHONE_OPTIONS_URL}/:legacyAccountId/:userId`}
          component={LegacyPhlebotomyRedirectPage}
        />
        <Route
          exact
          path={`${NIPT_CONSENT}/:portalAccountPublicId?`}
          component={PublicConsent}
        />
        <Redirect from="*" to={LANDING_PAGE_URL} />
      </Switch>
      {showFooter && <Footer showLanguageSwitcher={showLanguageSwitcher} />}
      {showAuthenticatedChrome && chatbotVariant === "whitelist" && (
        <ChatbotWidget isAggressiveChatbot={isAggressiveChatbot} />
      )}
    </>
  );
};
export default AppContainer;
